/**
 * Which member identity the Documents view should use, as ONE explicit
 * decision with no fall-through.
 *
 * This exists because the implicit version of this logic caused a
 * silently-blank Documents tab. Two independent traps fed it:
 *
 * 1. The page read the route id itself (`useParams().id`). Under the keep-alive
 *    tab shell, `App.tsx` mounts everything below a single catch-all
 *    `<Route path="/*">`, which declares no `:id` segment — so `useParams().id`
 *    is `undefined` for every component in the shell. The host must therefore
 *    pass the id down (`navId`); MemberProfilePage already resolves it via
 *    `navIdOverride`.
 * 2. `hasExplicitProps = !!(companyId && membMpiNo)` treated "half an identity"
 *    as "no identity", quietly downgrading a PP member to a self-fetch that
 *    can never succeed (a `pp:` route id has no Pathways record). That mattered
 *    because MemberProfilePage's PP branch always passes both props, but its
 *    `ppMpi` is `ppMember?.membMpiNo ?? ""`.
 *
 * Keeping this pure (no React, no Apollo) is deliberate: the decision is the
 * part that was wrong, and it is now testable without mounting anything.
 */
export type DocumentsIdentity =
  /** Host supplied a complete PP identity — skip the MEMBER_BY_ID self-fetch. */
  | { mode: "explicit"; companyId: string; membMpiNo: string }
  /**
   * Host supplied HALF a PP identity. Never silently self-fetch: report it.
   * `missing` names the absent half so the UI can say which.
   */
  | { mode: "partial"; missing: "membMpiNo" | "companyId" }
  /** No props — resolve `id` via MEMBER_BY_ID, then the Pathways→PP crosswalk. */
  | { mode: "self-fetch"; id: string }
  /** Nothing to resolve from at all — the case that used to render nothing. */
  | { mode: "no-member" };

export interface DocumentsIdentityInput {
  /** Route member id handed down by the host (preferred). */
  navId?: string;
  /** `useParams().id` — only meaningful outside the catch-all tab shell. */
  routeId?: string;
  companyId?: string;
  membMpiNo?: string;
}

export function resolveDocumentsIdentity({
  navId,
  routeId,
  companyId,
  membMpiNo,
}: DocumentsIdentityInput): DocumentsIdentity {
  // Explicit props win outright: a host that knows the PP identity is
  // authoritative, and the route id is irrelevant to it.
  if (companyId && membMpiNo) {
    return { mode: "explicit", companyId, membMpiNo };
  }
  // Half an identity is an error, NOT a reason to self-fetch.
  if (companyId || membMpiNo) {
    return { mode: "partial", missing: companyId ? "membMpiNo" : "companyId" };
  }
  const id = navId ?? routeId;
  return id ? { mode: "self-fetch", id } : { mode: "no-member" };
}
